import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { lazy, Suspense, useEffect, useState } from "react";
import Home from "./pages/Home";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import { CookieConsentProvider } from "./context/Cookie/CookieConsentContext";
import CookieConsentBanner from "./components/CookieConsentBanner";
import ScrollToTop from "./components/ScrollToTop";
import "./lang/i18n";
import { useTranslation } from "react-i18next";
import { SnackbarProvider } from "notistack";
import ResetScroll from "./helper/ResetScroll";
import { HelmetProvider } from "react-helmet-async";
import GeoRedirect from "./routes/GeoRedirect";
import { fetchAPI } from "./helper/fetchAPI";
import * as constants from "./constants";
import { useCountryPrefix } from "./routes/useCountryPrefix";
import { PageLoadingProvider, usePageLoading } from "./context/PageLoadingContext";

const AboutUs = lazy(() => import("./pages/AboutUs"));
const ContactUs = lazy(() => import("./pages/ContactUs"));
const PropertyListing = lazy(() => import("./pages/PropertyListing"));
const PropertyDetail = lazy(() => import("./pages/PropertyDetail"));
const Career = lazy(() => import("./pages/Career"));
const Offer = lazy(() => import("./pages/Offer"));
const Area = lazy(() => import("./pages/Area"));
const Developer = lazy(() => import("./pages/Developer"));
const DeveloperProject = lazy(() => import("./pages/DeveloperProject"));
const News = lazy(() => import("./pages/News"));
const NewsDetails = lazy(() => import("./pages/NewsDetails"));
const Faq = lazy(() => import("./pages/Faq"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsAndConditions = lazy(() => import("./pages/TermsAndConditions"));
const Map = lazy(() => import("./pages/Map"));
const NewLaunch = lazy(() => import("./pages/NewLaunch"));
const Growth = lazy(() => import("./pages/Growth"));
const Partner = lazy(() => import("./pages/Partner"));
const PageNotFound = lazy(() => import("./pages/PageNotFound"));

const LazyPage = ({ children }) => <Suspense fallback={null}>{children}</Suspense>;

function App() {
  const { i18n } = useTranslation();
  const language = i18n.language;

  useEffect(() => {
    document.documentElement.lang = language;
    document.documentElement.dir = language === "ar" ? "rtl" : "ltr";

    if (language === "ar") {
      const rtlStyles = [
        "/assets/css/bootstrap.rtl.min.css?v3.0",
        "/assets/css/style-rtl.css?v3.0",
        "/assets/css/responsive-rtl.css?v3.0",
      ];

      rtlStyles.forEach((href) => {
        const link = document.createElement("link");
        link.rel = "stylesheet";
        link.href = href;
        document.head.appendChild(link);
      });
    }

    return () => {
      document.querySelectorAll('link[rel="stylesheet"]').forEach((el) => {
        if (el.href.includes("rtl")) {
          el.remove();
        }
      });
    };
  }, [language]);

  const renderRoutes = () => [
    <Route path="about-us" element={<LazyPage><AboutUs /></LazyPage>} key="about-us" />,
    <Route path="contact" element={<LazyPage><ContactUs /></LazyPage>} key="contact" />,
    <Route path="search" element={<LazyPage><PropertyListing /></LazyPage>} key="search" />,
    <Route path="careers" element={<LazyPage><Career /></LazyPage>} key="career" />,
    <Route path="offer" element={<LazyPage><Offer /></LazyPage>} key="offer" />,
    <Route path="area/:cityName" element={<LazyPage><Area /></LazyPage>} key="area" />,
    <Route path="launch/:projectName/:idProject" element={<LazyPage><NewLaunch /></LazyPage>} key="launch" />,
    <Route path="developers" element={<LazyPage><Developer /></LazyPage>} key="developers" />,
    <Route path="developers/:developerName/:idDeveloper" element={<LazyPage><DeveloperProject /></LazyPage>} key="devProject1" />,
    <Route path="developers/:cityName/:developerName/:idDeveloper" element={<LazyPage><DeveloperProject /></LazyPage>} key="devProject2" />,
    <Route path="developers/:developerName/project/:projectName/:idProject" element={<LazyPage><PropertyListing /></LazyPage>} key="devProjListing" />,
    <Route path="developers/:developerName/project/:projectName/property/:idTitle/:idProperty" element={<LazyPage><PropertyDetail /></LazyPage>} key="devProjDetail" />,
    <Route path="blogs" element={<LazyPage><News /></LazyPage>} key="news" />,
    <Route path="blogs/:idSlug" element={<LazyPage><NewsDetails /></LazyPage>} key="newsDetail" />,
    <Route path="growth" element={<LazyPage><Growth /></LazyPage>} key="growth" />,
    <Route path="partner" element={<LazyPage><Partner /></LazyPage>} key="partner" />,
    <Route path="faq" element={<LazyPage><Faq /></LazyPage>} key="faq" />,
    <Route path="privacy-policy" element={<LazyPage><PrivacyPolicy /></LazyPage>} key="privacy" />,
    <Route path="terms-conditions" element={<LazyPage><TermsAndConditions /></LazyPage>} key="terms" />,
    <Route path="map" element={<LazyPage><Map /></LazyPage>} key="map" />,
    <Route path="*" element={<LazyPage><PageNotFound /></LazyPage>} key="notfound" />,
  ];

  return (
    <HelmetProvider>
      <SnackbarProvider maxSnack={1} autoHideDuration={2000}>
        <CookieConsentProvider>
          <Router>
            <ResetScroll />
            <PageLoadingProvider>
              <AppContent renderRoutes={renderRoutes} />
            </PageLoadingProvider>
          </Router>
        </CookieConsentProvider>
      </SnackbarProvider>
    </HelmetProvider>
  );
}

function AppContent({ renderRoutes }) {
  const countryPrefix = useCountryPrefix();
  const { isPageLoading } = usePageLoading();
  const { i18n } = useTranslation();

  const [mainSection, setMainPageSection] = useState();
  const [areaSection, setAreaSection] = useState();
  const [recommendedSection, setRecommendedSection] = useState();
  const [contactSection, setContactSection] = useState();
  const [mainMenu, setMainMenu] = useState();

  useEffect(() => {
    const fetchData = async () => {
      const isArabic = i18n.language === "ar";
      const language = isArabic ? "ar" : "en";
      let apiUrl = process.env.REACT_APP_API_URL;
      apiUrl = `${apiUrl}${constants.footersection}?language=${language}&region_id=${countryPrefix}`;
      const res = await fetchAPI(apiUrl, "Get");
      if (res?.status === 200) {
        setMainPageSection(res?.data?.footerSections?.mainPageSection);
        setAreaSection(res?.data?.footerSections?.areaSection);
        setRecommendedSection(res?.data?.footerSections?.recommendedSection);
        setContactSection(res?.data?.footerSections?.contactSection);
        setMainMenu(res?.data?.headerSections?.menuSection);
      }
    };

    fetchData();
  }, [countryPrefix, i18n.language]);

  return (
    <div className="App">
      <Navbar mainMenu={mainMenu} />
      <GeoRedirect />
      <Routes>
        <Route path="/:country/*">
          <Route index element={<Home />} />
          {renderRoutes()}
        </Route>
        <Route path="/:country/:lang/*">
          <Route index element={<Home />} />
          {renderRoutes()}
        </Route>
      </Routes>
      <CookieConsentBanner />
      <ScrollToTop />
      {!isPageLoading && (
        <Footer
          mainSection={mainSection}
          areaSection={areaSection}
          recommendedSection={recommendedSection}
          contactSection={contactSection}
        />
      )}
    </div>
  );
}

export default App;
